import root from 'window-or-global';
import {ExecutionResult} from 'graphql';
import GraphQlService from 'epic-node-services/src/graphql/GraphQlService';
import {getValueIn, localeLanguageCode} from '../../utils/ValueUtils';
import {fetchOrGetCached, bulkFetchOrGetCached} from '../../utils/WebUtils';
import {CatalogItem as GraphqlCatalogItem, GetPriceRes, CatalogNamespace} from 'epic-graphql-types';
import {Namespace, GraphQlQuery} from '../../commonTypes';
import {chunk} from '../../utils/GeneralUtils';

interface OfferParams {
    namespace: string;
    offerId: string;
}

export interface CountryInfo {
    defaultCurrency: string;
    paymentCurrency: string;
    currencySymbolPlacement: string;
}

export type FetchCountryInfoResponse = ExecutionResult<CountryInfo>;

export interface CatalogOffer {
    namespace: string;
    id: string;
}

export interface FetchOffersResponse {
    data: CatalogOffer;
    errors: any;
    price: GetPriceRes;
    catalogNs: CatalogNamespace;
}

export type FetchOffersServiceResponse = ExecutionResult<FetchOffersResponse>;

interface CountryAgeFiltering {
    country: string;
    hasCountryFilter: boolean;
    ageGroup: number;
}

export type CountryAgeFilteringResponse = ExecutionResult<CountryAgeFiltering>;

export interface CatalogParams {
    namespace: string;
    id: string;
}

export type FetchCatalogItemResponse = ExecutionResult<GraphqlCatalogItem>;

export const CATALOG_ITEM_QUERY = `{
        id
        namespace
        title
        keyImages {
            type
            url
        }
        categories {
            path
        }
        releaseInfo {
            platform
        }
        customAttributes {
            key
            value
        }
        dlcItemList {
            id
        }
        mainGameItem {
            id
        }
    }
`;

const CATALOG_QUERY = `
    query catalogQuery($namespace: String!, $id: String!, $locale: String) {
        Catalog {
            catalogItem(namespace: $namespace, id: $id, locale: $locale)
                ${CATALOG_ITEM_QUERY}
        }
    }
`;

const catalogQueryReducer = response => {
    const catalogItem: GraphqlCatalogItem =
        getValueIn(response, 'data', 'Catalog', 'catalogItem') || {};
    return {data: catalogItem, errors: response && response.errors};
};

const PREREQUISITES_QUERY = `
    query fetchPrerequisites($offerParams: [OfferParams]) {
        Launcher{
            prerequisites(offerParams:$offerParams) {
                namespace,
                offerId,
                missingPrerequisiteItems
                satisfiesPrerequisites
            }
        }
    }
`;

export type PrerequisiteServiceResponse = ExecutionResult<any[]>;

export interface OfferOwnedStatus {
    entitledToAllItemsInOffer: boolean;
    entitledToAnyItemInOffer: boolean;
    namespace: string;
    offerId: string;
}

export type OfferOwnedStatusResponse = ExecutionResult<OfferOwnedStatus>;

const OFFER_OWNED_QUERY = `
query launcherQuery($namespace:String!, $offerId:String!) {
    Launcher {
        entitledOfferItems(namespace: $namespace, offerId: $offerId) {
            namespace
            offerId
            entitledToAllItemsInOffer
            entitledToAnyItemInOffer
        }
    }
}
`;

class CatalogApi {
    async fetchStorefront(namespace = 'epic', locale = 'en', count = 100, country = 'US') {
        const executeQuery = () =>
            GraphQlService.query(
                `
            query catalogQuery($namespace:String!, $locale:String, $count:Int, $country: String!) {
                Catalog {
                    catalogOffers(
                        namespace: $namespace, 
                        locale: $locale, 
                        params: {count: $count, country: $country}) {
                        elements {
                            isFeatured
                            collectionOfferIds
                            ${this.getStoreViewCatalogOfferGql()}
                        }
                    }
                }
            }
        `,
                {namespace, locale, count, country},
                false
            );

        const response = await fetchOrGetCached(
            executeQuery,
            `store-${namespace}-${locale}-${count}`
        );
        const storefrontItems = getValueIn(response, 'data', 'Catalog', 'catalogOffers') || [];
        return {data: storefrontItems, errors: response.errors};
    }

    async fetchCountryInfo(countryCode: string = 'US'): Promise<FetchCountryInfoResponse> {
        const response = await GraphQlService.query(
            `
            query countryData($countryCode:String!) {
                Catalog {
                    countryData(countryCode: $countryCode) {
                        defaultCurrency
                        paymentCurrency
                        currencySymbolPlacement
                    }
                }
            }`,
            {countryCode}
        );

        const data: CountryInfo = getValueIn(response, 'data', 'Catalog', 'countryData') || {};
        return {data, errors: response.errors};
    }

    offerQuery: string = `
        query catalogQuery(
            $productNamespace:String!, 
            $offerId:String!, 
            $locale:String, 
            $country:String!, 
            $lineOffers: [LineOfferReq]!) {
                Catalog {
                    catalogOffer(namespace: $productNamespace, 
                        id: $offerId, 
                        locale: $locale) {
                            namespace
                            effectiveDate
                            id
                            customAttributes {
                                key
                                value
                            }
                            items {
                                id
                                status
                                customAttributes {
                                    key
                                    value
                                }
                            }
                    }
                }
                PriceEngine {
                    price(country: $country, lineOffers: $lineOffers) {
                        totalPrice {
                            discountPrice
                            originalPrice
                            voucherDiscount
                            discount
                            fmtPrice(locale: $locale) {
                                originalPrice
                                discountPrice
                                intermediatePrice
                            }
                        }
                        lineOffers {
                            appliedRules {
                                discountSetting {
                                    discountType
                                }
                            }
                        }
                    }
                }
            }
        `;

    async fetchBulkOffers(
        country,
        offers,
        calculateTax = false,
        locale = 'en'
    ): Promise<FetchOffersServiceResponse[]> {
        const batchQueries = offers.map(offer => {
            const {id, namespace} = offer;
            return {
                query: this.offerQuery,
                variables: {
                    productNamespace: namespace,
                    offerId: id,
                    locale,
                    country,
                    lineOffers: [{offerId: id, quantity: 1}],
                    calculateTax
                }
            };
        });

        const responses: FetchOffersServiceResponse[] = await GraphQlService.batchQuery(
            batchQueries
        );

        return responses.map(response => {
            const price = getValueIn(response, 'data', 'PriceEngine', 'price');
            const productOffer = getValueIn(response, 'data', 'Catalog', 'catalogOffer') || [];
            const catalogNs = getValueIn(response, 'data', 'Catalog', 'catalogOffer', 'namespace');
            return {data: productOffer, errors: response.errors, price, catalogNs};
        });
    }

    async fetchOffer(
        country,
        productNamespace,
        offerId,
        calculateTax = false,
        locale = 'en'
    ): Promise<any> {
        const response = await GraphQlService.query(this.offerQuery, {
            productNamespace,
            offerId,
            locale,
            country,
            lineOffers: [{offerId, quantity: 1}],
            calculateTax
        });

        const price = getValueIn(response, 'data', 'PriceEngine', 'price');
        const productOffer = getValueIn(response, 'data', 'Catalog', 'catalogOffer') || [];
        const catalogNs = getValueIn(response, 'data', 'Catalog', 'catalogNs');
        return {data: productOffer, errors: response.errors, price, catalogNs};
    }

    async fetchCollectionOffers(offerId, productNamespace = 'epic', locale = 'en', country = 'US') {
        const response = await GraphQlService.query(
            `
            query catalogQuery($productNamespace:String!, $offerId:String!, $locale:String, $country:String!) {
                Catalog {
                    catalogOffer(namespace: $productNamespace, id: $offerId, locale: $locale) {
                        title
                        collectionOffers {
                            ${this.getStoreViewCatalogOfferGql()}
                        }
                    }
                }
            }
        `,
            {
                productNamespace,
                offerId,
                locale,
                country
            }
        );

        const data = getValueIn(response, 'data', 'Catalog', 'catalogOffer') || {};
        return {data, errors: response.errors};
    }

    async fetchCatalogItem(
        namespace: string,
        id: string,
        locale: string = 'en'
    ): Promise<FetchCatalogItemResponse> {
        const executeQuery = () =>
            GraphQlService.query(CATALOG_QUERY, {
                namespace,
                id,
                locale: localeLanguageCode(locale)
            });

        const response = await fetchOrGetCached(
            executeQuery,
            `common-catalogItem-${namespace}-${id}`
        );
        return catalogQueryReducer(response);
    }

    async bulkFetchCatalogItems(
        catalogParams: CatalogParams[],
        locale
    ): Promise<FetchCatalogItemResponse[]> {
        const bulkQuery: GraphQlQuery[] = catalogParams.map(({id, namespace}) => ({
            query: CATALOG_QUERY,
            variables: {
                id,
                namespace,
                locale: localeLanguageCode(locale)
            }
        }));

        // TODO define proper type for response
        let responses: any[] = [];
        // split into chunk of 20 to get past bulk graphql query limit
        const parts: GraphQlQuery[][] = chunk(bulkQuery, 20);
        let index = parts.length;
        while (index--) {
            // create a fetchOrGetCached config array
            const config: any[] = parts[index].map(x => ({
                key: `common-catalogItem-${x.variables.namespace}-${x.variables.id}`,
                fetchFn: () => GraphQlService.batchQuery(x)
            }));
            // run query or pull from local cache
            const responsesPart = await bulkFetchOrGetCached(config);
            // collect
            responses = [...responses, ...responsesPart];
        }
        return responses.map(catalogQueryReducer);
    }

    async autocompleteCatalogOffers(
        namespace: string = 'epic',
        locale: string,
        query: string,
        country: string
    ): Promise<CatalogOffer[]> {
        const response = await GraphQlService.query(
            `
        query searchQuery(
            $namespace: String!, 
            $locale: String!, 
            $country: String!, 
            $query: String!
            )
        { Catalog {
            catalogOffers(namespace: $namespace, locale: $locale, params: {
              keywords: $query,
              country: $country
            }) {
                elements {
                  url
                  title
                  id
                  price(country: $country) {
                    totalPrice {
                      discountPrice
                      originalPrice
                      voucherDiscount
                      discount
                      fmtPrice(locale: $locale) {
                        originalPrice
                        discountPrice
                        intermediatePrice
                      }
                    }
                  }
                  productSlug
                  categories {
                    path
                  }
                }
              }
            }
          }
        `,
            {namespace, locale, query, country}
        );

        const catalogOffers: CatalogOffer[] = getValueIn(
            response,
            'data',
            'Catalog',
            'catalogOffers',
            'elements'
        );
        return catalogOffers;
    }

    async searchCatalogOffers(
        namespace: string = 'epic',
        locale: string,
        query: string,
        country: string
    ): Promise<CatalogOffer[]> {
        const response = await GraphQlService.query(
            `
            query catalogQuery(
                $namespace:String!, 
                $locale:String, 
                $count:Int, 
                $country: String!, 
                $query: String!) {
                Catalog {
                    catalogOffers(
                        namespace: $namespace, 
                        locale: $locale, 
                        params: {count: $count, country: $country, keywords: $query}) {
                        elements {
                            collectionOfferIds
                            ${this.getStoreViewCatalogOfferGql()}
                        }
                    }
                }
            }
            `,
            {namespace, locale, query, country}
        );
        const searchResults: CatalogOffer[] =
            getValueIn(response, 'data', 'Catalog', 'catalogOffers', 'elements') || [];
        return searchResults;
    }

    async fetchLatestCatalogUpdateTimestamp(): Promise<string> {
        const response = await GraphQlService.query(
            `
            query getLastCatalogUpdate
            {
                Catalog {
                    lastCatalogUpdate
                }
            }
            `
        );
        const lastCatalogUpdate: string = getValueIn(
            response,
            'data',
            'Catalog',
            'lastCatalogUpdate'
        );
        return lastCatalogUpdate;
    }

    async fetchCountryAgeFiltering(): Promise<CountryAgeFilteringResponse> {
        const response = await GraphQlService.query(
            `
                query
                {
                    Catalog {
                      myCountryFilter {
                        country
                        hasCountryFilter
                        ageGroup
                      }
                    }
                  }

            `
        );
        const myCountryFilter = getValueIn(response, 'data', 'Catalog', 'myCountryFilter');
        return {data: myCountryFilter, errors: response.errors};
    }

    getStoreViewCatalogOfferGql() {
        return `
          title
          id
          namespace
          description
          keyImages {
            type
            url
          }
          items {
            id
            namespace
          }
          customAttributes {
            key
            value
          }
          categories {
            path
          }
          price(country: $country) {
            totalPrice {
              discountPrice
              originalPrice
              voucherDiscount
              discount
              fmtPrice(locale: $locale) {
                originalPrice
                discountPrice
                intermediatePrice
              }
            }
          }
          linkedOfferId
          linkedOffer {
            effectiveDate
            customAttributes {
              key
              value
            }
          }
        `;
    }

    async fetchNamespace(
        namespace: string,
        checkAccountAuth: boolean = false
    ): Promise<CatalogNamespace> {
        const response = await GraphQlService.query(
            `
            query getNs($namespace: String!){
                Catalog {
                    catalogNs(namespace: $namespace) {
                        ageGating {
                            age
                            country
                        }
                        ${checkAccountAuth ? 'accountAuthorized' : ''}
                    }
                }
            }
        `,
            {namespace}
        );
        const ag: CatalogNamespace = getValueIn(response, 'data', 'Catalog', 'catalogNs');
        return ag;
    }

    async fetchProductPrerequisites(
        offerParams: OfferParams
    ): Promise<PrerequisiteServiceResponse> {
        const response = await GraphQlService.query(PREREQUISITES_QUERY, {offerParams});
        const data: any[] = getValueIn(response, 'data', 'Launcher', 'prerequisites') || [];
        return {data, errors: response.errors};
    }

    async fetchBulkOfferOwnedStatus(
        offers: CatalogOffer[] = []
    ): Promise<OfferOwnedStatusResponse[]> {
        const bulkQueryArray = offers.map(offer => ({
            query: OFFER_OWNED_QUERY,
            variables: {
                namespace: offer.namespace,
                offerId: offer.id
            }
        }));

        const response = await GraphQlService.batchQuery(bulkQueryArray);

        return response.reduce((acc, offerResponse) => {
            acc.push({
                data: getValueIn(offerResponse, 'data', 'Launcher', 'entitledOfferItems') || [],
                errors: offerResponse.errors
            });
            return acc;
        }, []);
    }

    async fetchOfferOwnedStatus(
        namespace: Namespace,
        offerId: string
    ): Promise<OfferOwnedStatusResponse> {
        const response = await GraphQlService.query(OFFER_OWNED_QUERY, {namespace, offerId});

        const offerOwnedData: OfferOwnedStatus =
            getValueIn(response, 'data', 'Launcher', 'entitledOfferItems') || {};
        return {data: offerOwnedData, errors: response.errors};
    }
}

// Enforce singleton even if multiple versions of this module are loaded
if (!root.__CatalogApi) {
    root.__CatalogApi = new CatalogApi();
}
export default root.__CatalogApi;
